import { PlainButton } from "@jugl-web/ui-components/cross-platform/PlainButton";
import { cx } from "@jugl-web/utils";
import { isColorDark } from "@jugl-web/utils/color";
import { CSSProperties, forwardRef, useState } from "react";
import {
  ChatBotUIConversationMessage,
  ChatBotUIProduct,
  ChatBotUISendMessagePayload,
} from "../../types";
import { ChatControlButton } from "../ChatControlButton";
import { ReactComponent as CopyIcon } from "./assets/copy.svg";
import { ReactComponent as EditIcon } from "./assets/edit.svg";
import { ChatBotDateSlotBooking } from "./components/ChatBotDateSlotBooking";
import { ChatBotInputsForm } from "./components/ChatBotInputsForm";
import { ChatBotMessageBody } from "./components/ChatBotMessageBody";
import { ChatBotProductList } from "./components/ChatBotProductList";
import {
  AVATAR_BUBBLE_OFFSET,
  AVATAR_SIZE,
  ChatMessageBubble,
} from "./components/ChatMessageBubble";
import { QuickRepliesList } from "./components/QuickRepliesList";

export interface ChatBotMessageComponentProps {
  message: ChatBotUIConversationMessage;
  isLast: boolean;
  userBubbleColor: string;
  onSendMessage?: (payload: ChatBotUISendMessagePayload) => void;
  onOpenProductDetailsDrawer?: (product: ChatBotUIProduct) => void;
  // Message actions
  onRevise?: () => void;
  onCopy?: () => void;
}

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const ChatBotMessageComponent = forwardRef<
  HTMLDivElement,
  ChatBotMessageComponentProps
>(
  (
    {
      message,
      isLast,
      userBubbleColor,
      onSendMessage,
      onOpenProductDetailsDrawer,
      onRevise,
      onCopy,
    },
    ref
  ) => {
    const [footerEndSlotElement, setFooterEndSlotElement] =
      useState<HTMLDivElement | null>(null);

    // Incoming means the message is from the bot / customer support
    // Outgoing means the message is from the widget consumer
    const isOutgoing = message.type === "outgoing";

    const quickReplies =
      message.payload?.type === "text" ? message.payload.quickReplies : [];

    const formattedTimestamp = formatTime(message.timestamp);

    const outsideBubblePaddingStyles: CSSProperties = {
      paddingRight: isOutgoing ? AVATAR_SIZE + AVATAR_BUBBLE_OFFSET : undefined,
      paddingLeft: isOutgoing ? undefined : AVATAR_SIZE + AVATAR_BUBBLE_OFFSET,
    };

    const isTextColorLight = isColorDark(userBubbleColor);

    const hasActions = !!onRevise || !!onCopy;
    const shouldShowFooter = isLast || hasActions;

    return (
      <div
        ref={ref}
        className={cx("flex w-full flex-col", {
          "items-end": isOutgoing,
          "items-start": !isOutgoing,
        })}
      >
        <ChatMessageBubble
          isOutgoing={isOutgoing}
          userBubbleColor={userBubbleColor}
          senderName={message.senderName}
          senderImageUrl={message.senderImageUrl}
          title={formattedTimestamp}
        >
          <div
            className={cx(
              "font-secondary m-0 break-words text-sm leading-[1.5]",
              {
                "text-white": isOutgoing && isTextColorLight,
                "text-black": isOutgoing && !isTextColorLight,
                "text-dark": !isOutgoing,
              }
            )}
          >
            <ChatBotMessageBody
              text={message.payload.text}
              whiteLinks={isOutgoing && isTextColorLight}
            />
          </div>
          {!isOutgoing && isLast && quickReplies.length > 0 && (
            <QuickRepliesList
              replies={quickReplies}
              onSelect={(reply) =>
                onSendMessage?.({ type: "text", text: reply })
              }
            />
          )}
        </ChatMessageBubble>

        {/* Inputs form */}
        {isLast && message.payload.type === "inputs" && (
          <ChatBotInputsForm
            inputs={message.payload.inputs}
            className="mt-2"
            style={{
              ...outsideBubblePaddingStyles,
              maxWidth: `min(calc(100% - ${
                AVATAR_SIZE + AVATAR_BUBBLE_OFFSET
              }px), 400px)`,
            }}
            onSubmit={(data) => onSendMessage?.({ type: "submit-form", data })}
          />
        )}

        {/* Products list */}
        {isLast && message.payload.type === "productList" && (
          <ChatBotProductList
            products={message.payload.products}
            navigationSlotElement={footerEndSlotElement}
            className="mt-2"
            onSelectProduct={(product) => onOpenProductDetailsDrawer?.(product)}
          />
        )}

        {/* Date slot booking */}
        {isLast && message.payload.type === "dateSlotBooking" && (
          <ChatBotDateSlotBooking
            businessTimezoneCode={message.payload.timezoneCode}
            slots={message.payload.slots}
            className="mt-2"
            style={{
              ...outsideBubblePaddingStyles,
              maxWidth: `min(calc(100% - ${
                AVATAR_SIZE + AVATAR_BUBBLE_OFFSET
              }px), 400px)`,
            }}
          />
        )}

        {/* Message footer */}
        {shouldShowFooter && (
          <div
            className={cx(
              "mt-2 flex w-full items-center justify-between gap-2",
              isOutgoing && "flex-row-reverse"
            )}
          >
            {/* Footer start */}
            <div className="flex items-center gap-2">
              {/* Timestamp (visible for last message only) */}
              {isLast && (
                <div
                  className="font-secondary text-dark-600 text-xs leading-[1.4]"
                  style={outsideBubblePaddingStyles}
                >
                  {formattedTimestamp}
                </div>
              )}
              {/* Message actions */}
              {hasActions && (
                <div
                  className="flex items-center gap-2"
                  style={isLast ? undefined : outsideBubblePaddingStyles}
                >
                  {isLast && (
                    <div className="bg-dark-100 h-[20px] w-px shrink-0" />
                  )}

                  {onRevise && (
                    <ChatControlButton
                      title="Revise answer"
                      className="h-7 w-7"
                      onClick={onRevise}
                    >
                      <EditIcon />
                    </ChatControlButton>
                  )}

                  {onCopy && (
                    <PlainButton
                      title="Copy message"
                      className="h-7 w-7"
                      onClick={onCopy}
                    >
                      <CopyIcon />
                    </PlainButton>
                  )}
                </div>
              )}
            </div>
            {/* Footer end */}
            <div ref={setFooterEndSlotElement} />
          </div>
        )}
      </div>
    );
  }
);
